import profile from '@/config/profile';
import { PROFILE_ENV } from '@/config';
import React, { useState, useEffect, useRef } from 'react';

const WhatIDoSection = () => {
  const [isVisible, setIsVisible] = useState(false);
  const sectionRef = useRef(null);
  const isLightTheme = profile?.theme === 'light';

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.2 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => {
      if (sectionRef.current) {
        observer.unobserve(sectionRef.current);
      }
    };
  }, []);



  return (
    <section 
      id="work" 
      ref={sectionRef}
      className="min-h-screen flex flex-col justify-center px-8 py-20 relative"
    >
      <div className="max-w-6xl mx-auto w-full">
        <h2 className={`text-sm font-light tracking-widest mb-16 transition-all duration-1000 transform ${
          isLightTheme ? 'text-slate-600' : 'text-gray-400'
        } ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'
        }`}>
          WHAT I DO
        </h2>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
          {profile?.servicesProvided?.map((service, index) => (
            <div
              key={service.title}
              className={`group cursor-pointer transition-all duration-1000 transform ${
                isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
              }`}
              style={{ transitionDelay: `${service.delay}ms` }}
            >
              <div className={`relative overflow-hidden ${
                service.highlight ? 'bg-blue-500' : isLightTheme ? 'bg-white border border-slate-200' : 'bg-gray-900'
              } p-8 rounded-lg hover:scale-105 transition-all duration-500 group-hover:shadow-2xl`}>
                {service.highlight && (
                  <div className="absolute inset-0 bg-blue-500 opacity-90"></div>
                )}
                
                <div className="relative z-10">
                  <h3 className={`text-2xl md:text-3xl font-bold mb-4 ${
                    service.highlight ? 'text-black' : isLightTheme ? 'text-slate-900' : 'text-white'
                  } group-hover:scale-110 transition-transform duration-300`}>
                    {service.title}
                  </h3>
                  
                  <p className={`text-sm leading-relaxed ${
                    service.highlight ? 'text-black opacity-80' : isLightTheme ? 'text-slate-600' : 'text-gray-400'
                  } group-hover:text-opacity-100 transition-all duration-300`}>
                    {service.description}
                  </p>
                  
                  <div className={`w-12 h-1 ${
                    service.highlight ? 'bg-black' : 'bg-blue-500'
                  } mt-6 group-hover:w-20 transition-all duration-500`}></div>
                </div>

                {/* Hover effect overlay */}
                <div className="absolute inset-0 bg-white opacity-0 group-hover:opacity-5 transition-opacity duration-500"></div>
              </div>
            </div>
          ))}
        </div>

        {/* Expertise section */}
        <div className={`mt-20 transition-all duration-1000 delay-1000 transform ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
        }`}>
          <div className="space-y-12">
            {profile?.expertise?.map((item, index) => (
              <div key={index} className="flex flex-col space-y-4">
                <h3 className={`text-2xl font-bold ${isLightTheme ? 'text-slate-900' : 'text-white'}`}>{item.title}</h3>
                <p className={isLightTheme ? 'text-slate-600' : 'text-gray-400'}>{item.description}</p>
              </div>
            ))}
          </div>
        </div>

        {/* Bottom section with additional info */}
        <div className={`mt-20 transition-all duration-1000 delay-1000 transform ${
          isVisible ? 'translate-y-0 opacity-100' : 'translate-y-20 opacity-0'
        }`}>
          <div className="relative">
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-8">
              <span className={isLightTheme ? 'text-slate-900' : 'text-white'}>Empowering </span>
              <span className="text-blue-500">people </span>
              <span className={isLightTheme ? 'text-slate-900' : 'text-white'}>with meaningful</span>
            </h3>
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-8">
              <span className="text-blue-500">solutions </span>
              <span className={isLightTheme ? 'text-slate-900' : 'text-white'}>tailored to</span>
            </h3>
            <h3 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight">
              <span className={isLightTheme ? 'text-slate-900' : 'text-white'}>their unique needs.</span>
            </h3>
            
            <p className={`mt-8 max-w-md leading-relaxed ${isLightTheme ? 'text-slate-600' : 'text-gray-400'}`}>
              I am passionate about creating reliable, human-centered solutions that solve real-world challenges and improve outcomes.
              Let's build a better future together.
            </p>
          </div>
          
          {/* Side image */}
          <div className="absolute right-0 top-0 w-80 h-96 hidden lg:block">
            <img
              src={`/assets/img/side/what-i-do-section/${PROFILE_ENV}-image.png`}
              alt="side-image"
              className="w-full h-full object-cover rounded-lg opacity-80 hover:opacity-100 transition-opacity duration-500"
            />
          </div>
        </div>
      </div>
    </section>
  );
};

export default WhatIDoSection;